<template>
  <div>
    <div
      class="checklist-layout layout-spacing"
      data-locator="checklist-page"
      :style="{ 'left' : pageOffsetForNavDrawer }"
    >
      <ColResizer class="fill-height">
        <template #right>
          <div class="checklist-layout__list-wrapper">
            <div class="checklist-layout__list">
              <div class="checklist-layout__header row-gap-6">
                <ChecklistHeader />

                <template v-if="bulkActionsAreEnabled">
                  <ChecklistActionBar />
                </template>

                <ChecklistFiltersActive/>
              </div>

              <div class="checklist__table-wrapper mt-6">
                <template v-if="deleteChecklistDoubleValidationPending">
                  <v-skeleton-loader type="table-heading, table-tbody"/>
                </template>
                <template v-else>
                  <ChecklistTable />
                </template>
              </div>
            </div>
          </div>
        </template>
      </ColResizer>
    </div>

    <DocumentsTablePane :mnemo="roomMnemo"/>
    <DocumentsMoveSnackbar v-if="movementSnackbarIsOpen" is-checklist/>
    <ChecklistPane />
    <ChecklistShareByEmailPane />
    <ChecklistFiltersPane :mnemo="roomMnemo"/>
    <ChecklistVersioningPane :mnemo="roomMnemo"/>
    <DocumentsAISummaryPane />

    <DocumentsDownloadFilesDialog
      v-if="documentsDownloadFilesDialogIsOpen"
      :fileType="downloadFilesDialogFileType"
      :mnemo="roomMnemo"
      @close="CLOSE_DOCUMENTS_DOWNLOAD_FILES"
    />
    <ChecklistRenameAttachedFileDialog :mnemo="roomMnemo"/>
    <ChecklistShareTasksDialog v-if="shareTasksDialog.isOpen"
                               :mnemo="roomMnemo"
                               @close="onShareChecklistTasksDialogClose"
    />
    <SaveChecklistDialog v-if="saveChecklistDialogIsOpen"
                         :mnemo="roomMnemo"
                         @close="onSaveChecklistDialogClose"
    />
    <ImportChecklistDialog v-if="importChecklistDialogIsOpen"
                           :is-from-template-dialog="checklistTemplatesSelectedTab !== TAB_INDEX.UNSET"
                           @close="onImportChecklistDialogClose"
    />
    <ImportChecklistFromTemplateDialog v-if="importChecklistFromTemplateDialogIsOpen"
                                       @close="onImportChecklistFromTemplateDialogClose"
    />
    <SmartImportModal />
    <ChecklistGlobalAttachedFilesDialog
      v-if="globalAttachedFilesDialogIsOpen"
      :mnemo="roomMnemo"
      @close="onGlobalAttachedFilesDialogClose"
    />
    <DocumentsDeleteDialog
      v-if="documentsDeleteDialogIsOpen"
      @close="CLOSE_DOCUMENTS_DELETION_DIALOG"
      @validate-deletion="onDeletionValidation"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import ColResizer from '@/common/col-resizer/ColResizer.vue'
import { APP_HEADER_HEIGHT, ROOM_HEADER_EXTENSION_HEIGHT } from '@/common/constants'
import { PROJECT_NAV_DRAWER_WIDTHS } from '@/common/layout/constants'
import { waitForElement } from '@/common/utils/checklist'
import { handleElementDimensionResize } from '@/common/utils/dimensionObserver'
import { FILE_TYPES } from '@/common/utils/files'
import { getRoomRouteType } from '@/common/utils/rooms'
import { toast } from '@/common/utils/toast'
import ChecklistActionBar from '@/project/checklist/ChecklistActionBar.vue'
import ChecklistFiltersActive from '@/project/checklist/ChecklistFiltersActive.vue'
import ChecklistHeader from '@/project/checklist/ChecklistHeader.vue'
import ChecklistGlobalAttachedFilesDialog from '@/project/checklist/dialogs/ChecklistGlobalAttachedFilesDialog.vue'
import ChecklistRenameAttachedFileDialog from '@/project/checklist/dialogs/ChecklistRenameAttachedFileDialog.vue'
import ChecklistShareTasksDialog from '@/project/checklist/dialogs/ChecklistShareTasksDialog.vue'
import ImportChecklistDialog from '@/project/checklist/dialogs/ImportChecklistDialog.vue'
import ImportChecklistFromTemplateDialog from '@/project/checklist/dialogs/ImportChecklistFromTemplateDialog.vue'
import SaveChecklistDialog from '@/project/checklist/dialogs/SaveChecklistDialog.vue'
import SmartImportModal from '@/project/checklist/dialogs/SmartImportModal.vue'
import { TAB_INDEX } from '@/project/checklist/enums'
import ChecklistFiltersPane from '@/project/checklist/panes/ChecklistFiltersPane.vue'
import ChecklistPane from '@/project/checklist/panes/ChecklistPane.vue'
import ChecklistShareByEmailPane from '@/project/checklist/panes/ChecklistShareByEmailPane.vue'
import ChecklistVersioningPane from '@/project/checklist/panes/ChecklistVersioningPane.vue'
import ChecklistTable from '@/project/checklist/table/ChecklistTable.vue'
import DocumentsDeleteDialog from '@/project/documents/dialogs/DocumentsDeleteDialog.vue'
import DocumentsDownloadFilesDialog from '@/project/documents/dialogs/DocumentsDownloadFilesDialog.vue'
import {
  ROOM_MEMBERS_ROUTE_NAME,
  ROOM_CHECKLIST_ROUTE_NAME,
  ROOM_CHECKLIST_DELETION_VALIDATION_ROUTE_NAME,
} from '@/router/routerData'
import {
  CLOSE_SAVE_CHECKLIST_DIALOG,
  CLOSE_IMPORT_CHECKLIST_DIALOG,
  CLOSE_IMPORT_CHECKLIST_FROM_TEMPLATE_DIALOG,
  DELETE_CHECKLIST_DOUBLE_VALIDATION,
  GET_GLOBAL_TASK_FOLDER,
  GET_TASK_FOLDER,
  DELETE_FILDERS,
} from '@/store/modules/checklist/action_types'
import {
  RESET_CHECKLIST,
  RESET_SELECTED_ATTACHED_FILE,
  SET_SHARE_TASKS_DIALOG,
  SET_GLOBAL_ATTACHED_FILES_DIALOG_IS_OPEN,
} from '@/store/modules/checklist/mutation_types'
import { SET_MOVEMENT_SNACKBAR_IS_OPEN } from '@/store/modules/documents/mutation_types'
import { DocumentsDeleteActionTypes } from '@/store/modules/documents-delete/action_types'
import { DocumentsDeleteMutationTypes } from '@/store/modules/documents-delete/mutation_types'
import { CLOSE_DOCUMENTS_DOWNLOAD_FILES } from '@/store/modules/documents-download-files/action_types'

import DocumentsMoveSnackbar from '../documents/DocumentsMoveSnackbar.vue'
import DocumentsAISummaryPane from '../documents/pane/DocumentsAISummaryPane.vue'
import DocumentsTablePane from '../documents/pane/DocumentsTablePane.vue'

export default {
  name: 'Checklist',
  components: {
    ChecklistFiltersActive,
    ChecklistFiltersPane,
    ChecklistGlobalAttachedFilesDialog,
    ChecklistHeader,
    ChecklistPane,
    ChecklistRenameAttachedFileDialog,
    ChecklistShareByEmailPane,
    ChecklistShareTasksDialog,
    ChecklistTable,
    ChecklistActionBar,
    ChecklistVersioningPane,
    ColResizer,
    DocumentsAISummaryPane,
    DocumentsTablePane,
    DocumentsMoveSnackbar,
    DocumentsDeleteDialog,
    DocumentsDownloadFilesDialog,
    ImportChecklistDialog,
    ImportChecklistFromTemplateDialog,
    SaveChecklistDialog,
    SmartImportModal,
  },
  head () {
    return {
      title: this.$t('project.views.Checklist.metaTitle'),
    }
  },
  data () {
    return {
      TAB_INDEX,
      ROOM_CHECKLIST_ROUTE_NAME,
      ROOM_CHECKLIST_DELETION_VALIDATION_ROUTE_NAME,
      resizeObserverDestroyCallback: null,
    }
  },
  computed: {
    ...mapState('checklist', [
      'checklistTemplatesSelectedTab',
      'deleteChecklistDoubleValidationPending',
      'globalAttachedFilesDialogIsOpen',
      'importChecklistDialogIsOpen',
      'importChecklistFromTemplateDialogIsOpen',
      'saveChecklistDialogIsOpen',
      'shareTasksDialog',
    ]),
    ...mapGetters('checklist', ['selectedTasks', 'checklist']),
    ...mapGetters('room', ['roomMnemo']),
    ...mapState('room', ['currentRoom']),
    ...mapState('documents', ['movementSnackbarIsOpen']),
    ...mapState('documentsDownloadFiles', {
      documentsDownloadFilesDialogIsOpen: 'isOpen',
    }),
    ...mapState('documentsDelete', {
      documentsDeleteDialogIsOpen: 'isOpen',
      documentsToDelete: 'documents',
    }),
    ...mapState('global', ['mini']),
    FULL_HEADER_HEIGHT () {
      return APP_HEADER_HEIGHT + ROOM_HEADER_EXTENSION_HEIGHT
    },
    FULL_HEADER_HEIGHT_STYLE () {
      return `${this.FULL_HEADER_HEIGHT}px`
    },
    downloadFilesDialogFileType () {
      return FILE_TYPES.TODO
    },
    headerTop () {
      if (this.$vuetify.display.smAndDown) {
        return 64
      }
      if (this.$vuetify.display.lgAndUp) {
        return 76
      }
      return 72
    },
    bulkActionsAreEnabled () {
      return this.selectedTasks.length > 0
    },
    pageOffsetForNavDrawer () {
      if (this.$vuetify.display.mobile) {
        return 0
      }
      if (this.mini) {
        return PROJECT_NAV_DRAWER_WIDTHS.WITH_UNIT.FOLDED
      }
      return PROJECT_NAV_DRAWER_WIDTHS.WITH_UNIT.REGULAR
    },
  },
  created () {
    if (this.currentRoom?.hideTodo) {
      this.$router.replace({ name: ROOM_MEMBERS_ROUTE_NAME, params: { roomType: getRoomRouteType(this.currentRoom) } })
    } else if (this.$route.name === ROOM_CHECKLIST_DELETION_VALIDATION_ROUTE_NAME) {
      const cleanParams = (data) => {
        return data ? data.split(',').filter(item => item).map(id => parseInt(id)) : []
      }

      const data = {
        tasks: cleanParams(this.$route.query.taskIds),
        titles: cleanParams(this.$route.query.titleIds),
      }

      this.deleteChecklistDoubleValidation(data)
    }
    this.SET_MOVEMENT_SNACKBAR_IS_OPEN(false)
  },

  watch: {
    checklist: {
      immediate: false,
      handler (newVal) {
        if (newVal && newVal.length > 0 && !this.$vuetify.display.smAndDown) {
          this.setupChecklistTableResize()
        }
      },
    },
  },
  beforeUnmount () {
    this.RESET_CHECKLIST()
    if (this.resizeObserverDestroyCallback) {
      this.resizeObserverDestroyCallback()
    }
  },
  methods: {
    ...mapActions('documentsDelete', [DocumentsDeleteActionTypes.CLOSE_DOCUMENTS_DELETION_DIALOG]),
    ...mapActions('documentsDownloadFiles', [CLOSE_DOCUMENTS_DOWNLOAD_FILES]),
    ...mapActions('checklist', [
      CLOSE_IMPORT_CHECKLIST_DIALOG,
      CLOSE_IMPORT_CHECKLIST_FROM_TEMPLATE_DIALOG,
      CLOSE_SAVE_CHECKLIST_DIALOG,
      DELETE_CHECKLIST_DOUBLE_VALIDATION,
      DELETE_FILDERS,
      GET_GLOBAL_TASK_FOLDER,
      GET_TASK_FOLDER,
    ]),
    ...mapMutations('checklist', [
      RESET_CHECKLIST,
      RESET_SELECTED_ATTACHED_FILE,
      SET_GLOBAL_ATTACHED_FILES_DIALOG_IS_OPEN,
      SET_SHARE_TASKS_DIALOG,
    ]),
    ...mapMutations('documentsDelete', [DocumentsDeleteMutationTypes.SET_DOCUMENTS]),
    ...mapMutations('documents', [SET_MOVEMENT_SNACKBAR_IS_OPEN]),

    async deleteChecklistDoubleValidation (data) {
      const elements = data.tasks.concat(data.titles)
      try {
        await this.DELETE_CHECKLIST_DOUBLE_VALIDATION({
          mnemo: this.roomMnemo,
          data,
        })
        toast.success(this.$t('project.checklist.dialogs.ChecklistDeleteSelectionDialog.deleteSelectionSuccess', elements.length))
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.checklist.dialogs.ChecklistDeleteSelectionDialog.deleteSelectionError', elements.length))
      } finally {
        this.$router.replace({
          name: ROOM_CHECKLIST_ROUTE_NAME,
          params: {
            mnemo: this.roomMnemo,
            roomType: 'projects',
          },
        })
      }
    },
    onSaveChecklistDialogClose () {
      this.CLOSE_SAVE_CHECKLIST_DIALOG()
    },
    onImportChecklistDialogClose () {
      this.CLOSE_IMPORT_CHECKLIST_DIALOG()
    },
    onImportChecklistFromTemplateDialogClose () {
      this.CLOSE_IMPORT_CHECKLIST_FROM_TEMPLATE_DIALOG()
    },
    onGlobalAttachedFilesDialogClose () {
      this.SET_GLOBAL_ATTACHED_FILES_DIALOG_IS_OPEN(false)
    },
    onShareChecklistTasksDialogClose () {
      this.SET_SHARE_TASKS_DIALOG({
        isOpen: false,
        tasksToShare: [],
        parent: null,
      })
    },
    async onDeletionValidation () {
      try {
        await this.DELETE_FILDERS({
          mnemo: this.roomMnemo,
          data: {
            fileIds: this.documentsToDelete.map(document => document.id),
          },
        })

        toast.success(this.$t('project.views.Checklist.deleteFildersSuccess', this.documentsToDelete.length))

        this.RESET_SELECTED_ATTACHED_FILE()
        // Global delete operation
        if (this.globalAttachedFilesDialogIsOpen) {
          this.GET_GLOBAL_TASK_FOLDER(this.roomMnemo)
        } else {
          await this.GET_TASK_FOLDER({
            mnemo: this.roomMnemo,
            folderId: this.documentsToDelete[0]?.parent?.id,
            queryObject: { markRecent: true },
          })

          this.SET_DOCUMENTS([])
        }
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.views.Checklist.deleteFildersSuccess', this.documentsToDelete.length))
      } finally {
        this.CLOSE_DOCUMENTS_DELETION_DIALOG()
      }
    },

    async setupChecklistTableResize () {
      try {
        await waitForElement('.checklist-table')

        const checklistTableStructure = document.querySelector('.checklist-table')
        if (!checklistTableStructure) return

        this.resizeObserverDestroyCallback = handleElementDimensionResize(
          '.checklist-layout__header',
          'height',
          (newHeight) => {
            // newHeight is the dynamic height of all the elements above the card
            // + full header height
            // + extra padding between the card and table structure
            const extraPaddingEstimated = 122
            checklistTableStructure.style.maxHeight = `calc(100vh - ${newHeight + this.FULL_HEADER_HEIGHT + extraPaddingEstimated}px)`
          },
        )
      } catch (err) {
        console.error('checklist-table not found:', err)
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";

.checklist-layout {
  background-color: rgb(var(--v-theme-grey-lighten-6));
  height: calc(100% - v-bind(FULL_HEADER_HEIGHT_STYLE));
  position: fixed;
  right: 0;

  &__header {
    display: flex;
    flex-direction: column;
  }

  &__list-wrapper {
    position: relative;
    height: 100%;
    overflow: hidden;
  }

  &__list {
    position: absolute;
    top: 0;
    width: 100%;
  }
}
</style>
